import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col mt-4 gap-y-4"
}
const _hoisted_2 = /*#__PURE__*/_createStaticVNode("<div class=\"skeleton h-64\"></div><div class=\"flex justify-around\"><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div><div class=\"skeleton h-8 w-8\"></div></div>", 2)
const _hoisted_4 = [
  _hoisted_2
]
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h4", { class: "mb-2 mt-2 text-xs text-gray-400" }, "Precipitation (%)", -1)
const _hoisted_6 = { class: "flex gap-x-1 justify-around" }
const _hoisted_7 = { class: "flex flex-col align-center" }
const _hoisted_8 = { class: "text-xs mb-1.5" }
const _hoisted_9 = {
  style: {"font-size":"10px"},
  class: "text-center text-gray-400"
}

import { onMounted, ref, watch } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Legend,
  PointElement,
  LineElement,
  ScatterDataPoint,
  Title,
  Tooltip,
} from 'chart.js';
import { TChartData, TChartOptions } from 'vue-chartjs/dist/types';
import { getWeatherForecast3H5D } from '@/services/weather.services';
import { weatherLocation } from '@/states/homeState';
import { ForecastWeather } from '@/typings/services/weather';
import { format, isSameDay } from 'date-fns';


export default /*#__PURE__*/_defineComponent({
  __name: 'WeatherWidget',
  setup(__props) {

ChartJS.register(Tooltip, Title, Legend, LineElement, PointElement);

const loading = ref(true);
const currTimeIndex = ref();
const weatherCond = ref<ForecastWeather[]>();
const chartData = ref<TChartData<'line', ScatterDataPoint[], number>>({
  labels: [],
  datasets: [],
});

const chartOptions = ref<TChartOptions<'line'>>({
  responsive: true,
  plugins: {
    title: { display: true, text: '24hr Weather Forecast' },
    legend: { position: 'bottom' },
  },
  scales: {
    x: {
      type: 'time',
      time: { tooltipFormat: 'PP p' },
      grid: { drawOnChartArea: false },
      ticks: {
        source: 'labels',
        font: (ctx) => {
          const fontStyle = { size: 10 };
          if (ctx.index === currTimeIndex.value) {
            fontStyle['weight'] = 'bold';
          }
          return fontStyle;
        },
        callback: (v, i, ticks) => {
          if (ticks[i - 1] && !isSameDay(ticks[i - 1].value, ticks[i].value as number)) {
            return format(ticks[i].value, 'd/MM p');
          }

          return format(ticks[i].value, 'p') + (currTimeIndex.value === i ? '*' : '');
        },
      },
    },
    y: {
      title: { display: true, text: 'Temp. (°C)', font: { size: 10 } },
      grid: { drawOnChartArea: false },
      ticks: {
        callback: (v) => `${v}°`,
        font: {
          size: 11,
        },
      },
      offset: true,
    },
    clouds: {
      position: 'right',
      title: {
        display: true,
        text: 'Wind Speed (m/s)',
        font: { size: 10 },
      },
      grid: { display: false },
      ticks: {
        font: { size: 11 },
      },
    },
  },
});

watch(weatherLocation, () => {
  getData();
});

const getData = async () => {
  loading.value = true;
  const [lat, long] = weatherLocation.value;
  const result = await getWeatherForecast3H5D(lat, long);

  if (result.data.success) {
    chartData.value.labels = result.data.weather.list.map((weather) =>
      Date.parse(`${weather.from}Z`)
    );

    const timeDiff = chartData.value.labels[1] - chartData.value.labels[0];
    // getting the label from which current time to replace
    for (let v = 0; v < result.data.weather.list.length; v++) {
      const el = result.data.weather.list[v];

      if (Math.abs(Date.parse(`${el.from}Z`) - Date.now()) / timeDiff < 0.5) {
        chartData.value.labels.splice(v, 1, Date.now());
        currTimeIndex.value = v;
        break;
      }
    }

    chartData.value.datasets = [
      {
        data: result.data.weather.list.map((weather) => ({
          x: Date.parse(`${weather.from}Z`),
          y: parseFloat(weather.temperature.value),
        })),
        label: 'Temp. (°C)',
        borderWidth: 2,
        cubicInterpolationMode: 'monotone',
        tension: 0.5,
        borderColor: 'rgba(255, 102, 0, 0.13)',
        pointRadius: 2,
        pointBorderWidth: 0,
        pointHoverBackgroundColor: 'rgba(255, 102, 0, 0.13)',
        backgroundColor: '#f60',
      },
      {
        data: result.data.weather.list.map((weather) => ({
          x: Date.parse(`${weather.from}Z`),
          y: parseFloat(weather.windSpeed.mps),
        })),
        label: 'Wind Speed (m/s)',
        borderWidth: 2,
        cubicInterpolationMode: 'monotone',
        tension: 0.5,
        borderColor: 'rgb(0 118 255 / 13%)',
        pointRadius: 2,
        pointBorderWidth: 0,
        pointHoverBackgroundColor: 'rgb(0 118 255 / 13%)',
        backgroundColor: 'royalblue',
        yAxisID: 'clouds',
      },
    ];
    weatherCond.value = result.data.weather.list;
  }
  loading.value = false;
};

onMounted(() => {
  getData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_4))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(_unref(Line), {
            "chart-options": chartOptions.value,
            "chart-data": chartData.value
          }, null, 8, ["chart-options", "chart-data"]),
          _hoisted_5,
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weatherCond.value, (weather) => {
              return (_openBlock(), _createElementBlock("span", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, _toDisplayString((parseFloat(weather.precipitation.probability) * 100).toFixed(0)) + "%", 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(format)(Date.parse(`${weather.from}Z`), 'p')), 1)
              ]))
            }), 256))
          ])
        ], 64))
  ]))
}
}

})